import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { trackEvent } from "@/components/VisitTracker";
import BrandSelect from "@/components/BrandSelect";
import { api } from "@/lib/api";
import { Package, Plus, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import Skeleton from "@/components/Skeleton";
import Pagination from "@/components/Pagination";
import { useTranslation } from "react-i18next";
import DimensionSearch from "@/components/dims/DimensionSearch";
import DimMatchBadges from "@/components/dims/DimMatchBadges";

export default function CategoryPage() {
    const { categoryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, refreshCart } = useAuth();
    const { format: fmtMoney } = useCurrency();
    const { t } = useTranslation();

    const q = searchParams.get("q") || "";
    const page = parseInt(searchParams.get("page") || "1", 10);
    const perPage = parseInt(searchParams.get("per_page") || "25", 10);
    const sort = searchParams.get("sort") || "category";
    const inStockOnly = searchParams.get("in_stock") === "1";

    const brandId = searchParams.get("brand_id") || "";
    const amBrandId = searchParams.get("aftermarket_brand_id") || "";
    const dimsRaw = searchParams.get("dims") || "";
    const dims = useMemo(() => { try { return dimsRaw ? JSON.parse(dimsRaw) : {}; } catch { return {}; } }, [dimsRaw]);

    const [category, setCategory] = useState(null);
    const [allCategories, setAllCategories] = useState([]);
    const [allBrands, setAllBrands] = useState([]);
    const [products, setProducts] = useState(null);
    const [total, setTotal] = useState(0);
    const [adding, setAdding] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const [catRes, brandRes] = await Promise.all([
                    api.get("/categories"),
                    api.get("/brands"),
                ]);
                setAllCategories(catRes.data);
                setAllBrands(brandRes.data);
                if (categoryId) {
                    setCategory(catRes.data.find((c) => c.id === categoryId) || null);
                } else {
                    setCategory(null);
                }
            } catch {}
        })();
    }, [categoryId]);

    useEffect(() => {
        setProducts(null);
        (async () => {
            try {
                const params = { page, per_page: perPage, sort };
                if (categoryId) params.category_id = categoryId;
                if (brandId) params.brand_id = brandId;
                if (amBrandId) params.aftermarket_brand_id = amBrandId;
                if (q) params.q = q;
                if (inStockOnly) params.in_stock_only = true;
                if (dimsRaw) params.dims = dimsRaw;
                const countParams = { category_id: categoryId, brand_id: brandId, aftermarket_brand_id: amBrandId, q, in_stock_only: inStockOnly, dims: dimsRaw || undefined };
                const [listRes, countRes] = await Promise.all([
                    api.get("/products", { params }),
                    api.get("/products/count", { params: countParams }),
                ]);
                setProducts(listRes.data);
                setTotal(countRes.data.count);
                if (q && page === 1) trackEvent({ type: "search", q, hits: countRes.data.count });
            } catch {
                setProducts([]);
                setTotal(0);
            }
        })();
    }, [categoryId, brandId, amBrandId, q, page, perPage, sort, inStockOnly, dimsRaw]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === null || value === undefined || value === "") next.delete(key);
        else next.set(key, value);
        if (key !== "page") next.set("page", "1");
        setSearchParams(next);
    };

    const pages = useMemo(() => {
        const p = Math.max(1, Math.ceil(total / perPage));
        return p;
    }, [total, perPage]);

    const onAdd = async (productId) => {
        if (!user || user === false) {
            window.location.href = "/login";
            return;
        }
        setAdding((s) => ({ ...s, [productId]: "loading" }));
        try {
            await api.post("/cart/add", { product_id: productId, quantity: 1 });
            await refreshCart();
            setAdding((s) => ({ ...s, [productId]: "done" }));
            setTimeout(() => setAdding((s) => ({ ...s, [productId]: null })), 1500);
        } catch {
            setAdding((s) => ({ ...s, [productId]: null }));
        }
    };

    return (
        <div className="fx-bg min-h-[60vh]" data-testid="category-page">
            {/* Breadcrumb + Title */}
            <div className="fx-header">
                <div className="max-w-[1440px] mx-auto px-4 py-10 fx-reveal">
                    <div className="text-xs font-mono text-slate-500 mb-2">
                        <Link to="/" className="hover:text-[hsl(var(--primary))]">
                            {t("catalog.breadcrumb_home")}
                        </Link>
                        <span className="mx-2">/</span>
                        <Link to="/catalog" className="hover:text-[hsl(var(--primary))]">
                            {t("catalog.breadcrumb_catalog")}
                        </Link>
                        {category && (
                            <>
                                <span className="mx-2">/</span>
                                <span className="text-slate-900 uppercase">{category.name}</span>
                            </>
                        )}
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">
                        {q ? `${t("catalog.search_results")}: "${q}"` : category ? category.name : t("catalog.all_catalog")}
                    </h1>
                    <div className="mt-2 text-xs font-mono text-slate-500">
                        {total.toLocaleString()} {t("catalog.parts_count")} · {t("catalog.page_label")} {page} / {pages}
                    </div>
                </div>
            </div>

            {category?.dim_search?.enabled && (category.dim_search.fields || []).length > 0 && (
                <div className="max-w-[1440px] mx-auto px-4 pt-6">
                    <DimensionSearch
                        config={category.dim_search}
                        value={dims}
                        onApply={(v) => setParam("dims", Object.keys(v).length ? JSON.stringify(v) : "")}
                    />
                </div>
            )}
            <div className="max-w-[1440px] mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
                {/* Sidebar: Categories + Brands */}
                <aside className="space-y-4" data-testid="category-sidebar">
                    <div className="fx-panel fx-side">
                        <div className="px-3 py-2 border-b border-slate-200 label-kicker text-slate-600 bg-slate-50">
                            {t("catalog.categories")}
                        </div>
                        <div className="max-h-[400px] overflow-auto">
                            <Link
                                to="/catalog"
                                className={`block px-3 py-2 text-sm border-b border-slate-100 hover:bg-slate-50 ${
                                    !categoryId ? "bg-slate-900 text-white" : ""
                                }`}
                            >
                                {t("catalog.all")} ({allCategories.reduce((s, c) => s + (c.product_count || 0), 0)})
                            </Link>
                            {allCategories.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/catalog/${c.id}`}
                                    className={`flex items-center justify-between gap-2 px-3 py-2 text-sm border-b border-slate-100 hover:bg-slate-50 ${
                                        c.id === categoryId ? "bg-slate-900 text-white" : ""
                                    }`}
                                    data-testid={`sidebar-cat-${c.id}`}
                                >
                                    <span className="truncate">{c.name}</span>
                                    <span className={`font-mono text-xs ${c.id === categoryId ? "text-slate-300" : "text-slate-400"}`}>
                                        {c.product_count || 0}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {allBrands.filter((b) => b.kind === "oem").length > 0 && (
                        <div className="fx-panel fx-side" data-testid="sidebar-oem-brands">
                            <div className="px-3 py-2 border-b border-slate-200 label-kicker text-slate-600 bg-slate-50">
                                {t("catalog.brands_oem")}
                            </div>
                            <div className="p-2 flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => setParam("brand_id", "")}
                                    className={`text-[11px] font-mono px-2 py-1 border ${
                                        !brandId ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-slate-400"
                                    }`}
                                >
                                    {t("catalog.all")}
                                </button>
                                {allBrands.filter((b) => b.kind === "oem").map((b) => (
                                    <button
                                        key={b.id}
                                        onClick={() => setParam("brand_id", b.id === brandId ? "" : b.id)}
                                        className={`flex items-center gap-1 text-[11px] font-mono px-2 py-1 border ${
                                            b.id === brandId ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-slate-400"
                                        }`}
                                        data-testid={`sidebar-oem-brand-${b.id}`}
                                        title={b.name}
                                    >
                                        {b.logo_url && (
                                            <img src={b.logo_url} alt="" className="w-4 h-4 object-contain" />
                                        )}
                                        <span className="truncate max-w-[80px]">{b.name}</span>
                                        <span className={b.id === brandId ? "text-slate-300" : "text-slate-400"}>({b.product_count || 0})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {allBrands.filter((b) => b.kind === "aftermarket").length > 0 && (
                        <div className="fx-panel fx-side" data-testid="sidebar-aftermarket-brands">
                            <div className="px-3 py-2 border-b border-slate-200 label-kicker text-slate-600 bg-slate-50">
                                {t("catalog.brands_aftermarket")}
                            </div>
                            <div className="p-2 flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => setParam("aftermarket_brand_id", "")}
                                    className={`text-[11px] font-mono px-2 py-1 border ${
                                        !amBrandId ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 hover:border-slate-400"
                                    }`}
                                >
                                    {t("catalog.all")}
                                </button>
                                {allBrands.filter((b) => b.kind === "aftermarket").map((b) => (
                                    <button
                                        key={b.id}
                                        onClick={() => setParam("aftermarket_brand_id", b.id === amBrandId ? "" : b.id)}
                                        className={`flex items-center gap-1 text-[11px] font-mono px-2 py-1 border ${
                                            b.id === amBrandId ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 hover:border-slate-400"
                                        }`}
                                        data-testid={`sidebar-aftermarket-brand-${b.id}`}
                                        title={b.name}
                                    >
                                        {b.logo_url && (
                                            <img src={b.logo_url} alt="" className="w-4 h-4 object-contain" />
                                        )}
                                        <span className="truncate max-w-[80px]">{b.name}</span>
                                        <span className={b.id === amBrandId ? "text-amber-100" : "text-slate-400"}>({b.product_count || 0})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>

                {/* Main */}
                <main>
                    {/* Controls */}
                    <div className="fx-panel !overflow-visible relative z-20 p-3 flex flex-wrap items-center gap-3 mb-4 text-xs">
                        <BrandSelect categoryId={categoryId} value={brandId} onChange={(id) => setParam("brand_id", id)} />
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="filter-in-stock">
                            <input
                                type="checkbox"
                                checked={inStockOnly}
                                onChange={(e) => setParam("in_stock", e.target.checked ? "1" : "")}
                                className="w-4 h-4 accent-[hsl(var(--primary))]"
                            />
                            <span className="font-display font-bold uppercase tracking-wider">
                                {t("catalog.in_stock_only")}
                            </span>
                        </label>
                        <div className="ml-auto flex items-center gap-2">
                            <span className="label-kicker">{t("catalog.sort")}</span>
                            <select
                                value={sort}
                                onChange={(e) => setParam("sort", e.target.value)}
                                className="input-field !py-1.5 !text-xs !w-auto"
                                data-testid="sort-select"
                            >
                                <option value="category">{t("catalog.sort_category")}</option>
                                <option value="name">{t("catalog.sort_name_az")}</option>
                                <option value="-name">{t("catalog.sort_name_za")}</option>
                                <option value="stock">{t("catalog.sort_stock")}</option>
                            </select>
                            <span className="label-kicker ml-3">{t("catalog.per_page")}</span>
                            <select
                                value={perPage}
                                onChange={(e) => setParam("per_page", e.target.value)}
                                className="input-field !py-1.5 !text-xs !w-auto"
                                data-testid="per-page-select"
                            >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    <Pagination page={page} pages={pages} onChange={(n) => setParam("page", String(n))} position="top" />

                    {/* Product grid */}
                    {products === null ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <Skeleton key={i} className="aspect-[4/5]" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="fx-panel p-12 text-center">
                            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="font-mono text-slate-500">{t("catalog.no_results")}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid">
                            {products.map((p, i) => (
                                <div
                                    key={p.id}
                                    className="card-sharp flex flex-col fade-in-up"
                                    style={{ animationDelay: `${Math.min(i * 20, 300)}ms` }}
                                    data-testid={`product-card-${p.id}`}
                                >
                                    <Link to={`/product/${p.id}`} className="relative block bg-slate-50 aspect-square overflow-hidden">
                                        {page === 1 && i === 0 && p.dim_matches?.length > 0 && (
                                            <span className="absolute top-2 left-2 z-10 text-[10px] font-mono font-bold uppercase px-2 py-1 rounded bg-[hsl(var(--primary))] text-white shadow" data-testid="dim-best-match">
                                                ★ Më i sakti
                                            </span>
                                        )}
                                        {p.image_url ? (
                                            <img
                                                src={p.image_url}
                                                alt={p.name}
                                                className="w-full h-full object-contain p-4 hover:scale-105 transition-transform duration-300"
                                                loading="lazy"
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <Package className="w-12 h-12" />
                                            </div>
                                        )}
                                    </Link>
                                    <div className="p-3 flex-1 flex flex-col border-t border-slate-200">
                                        <Link to={`/product/${p.id}`} className="font-display font-bold text-sm uppercase tracking-tight line-clamp-2 clamp-2 hover:text-[hsl(var(--primary))]">
                                            {p.name}
                                        </Link>
                                        <div className="mt-1 font-mono text-[11px] text-slate-500">
                                            {p.category_name}
                                        </div>
                                        <DimMatchBadges product={p} />
                                        {(p.brand_name || p.aftermarket_brand_name) && (
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`product-brands-${p.id}`}>
                                                {p.brand_name && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-900 text-white text-[9px] font-mono uppercase tracking-wide" title={`OEM: ${p.brand_name}`}>
                                                        {p.brand_logo && <img src={p.brand_logo} alt="" className="w-3 h-3 object-contain bg-white p-[1px]" />}
                                                        {p.brand_name}
                                                    </span>
                                                )}
                                                {p.aftermarket_brand_name && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-[9px] font-mono uppercase tracking-wide" title={`Aftermarket: ${p.aftermarket_brand_name}`}>
                                                        {p.aftermarket_brand_logo && <img src={p.aftermarket_brand_logo} alt="" className="w-3 h-3 object-contain" />}
                                                        {p.aftermarket_brand_name}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className="mt-2 flex items-center justify-between">
                                            <span
                                                className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${
                                                    p.stock === "in_stock"
                                                        ? "bg-emerald-100 text-emerald-800"
                                                        : "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                {p.stock === "in_stock" ? `● ${t("product.in_stock")}` : `○ ${t("product.on_order")}`}
                                            </span>
                                            {p.stock_qty !== undefined && p.stock_qty !== null && (
                                                <span
                                                    className="text-[10px] font-mono font-bold text-slate-700"
                                                    data-testid={`stock-qty-${p.id}`}
                                                >
                                                    {p.stock_qty} {t("product.pcs")}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                                            <div className="text-sm font-display font-bold">
                                                {p.price ? (
                                                    <span>{fmtMoney(p.price)}</span>
                                                ) : (
                                                    <span className="text-slate-500 text-xs font-mono">{t("product.ask_admin")}</span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => onAdd(p.id)}
                                                disabled={adding[p.id] === "loading"}
                                                className="btn-outline !text-[11px] !py-1.5 !px-2 shrink-0"
                                                data-testid={`add-to-cart-${p.id}`}
                                            >
                                                {adding[p.id] === "done" ? (
                                                    <Check className="w-3.5 h-3.5" />
                                                ) : (
                                                    <Plus className="w-3.5 h-3.5" />
                                                )}
                                                {t("product.add_to_cart")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <Pagination page={page} pages={pages} onChange={(n) => setParam("page", String(n))} />
                </main>
            </div>
        </div>
    );
}
